import { createContext, useContext, ReactNode, useEffect } from 'react';
import { useAppConfig } from '@/contexts/AppConfigContext';
import { useCurrencyStore } from '@/stores/useCurrencyStore';

interface RegionalConfigContextValue {
  defaultCurrency: string | undefined;
  defaultCountry: string | undefined;
  timezone: string | undefined;
}

const RegionalConfigContext = createContext<RegionalConfigContextValue | undefined>(undefined);

/**
 * Regional Configuration Provider
 * Provides global access to regional settings throughout the application
 * 
 * Usage:
 * Wrap your app with this provider:
 * <RegionalConfigProvider>
 *   <App />
 * </RegionalConfigProvider>
 * 
 * Then use the hook in any component:
 * const { config, getCountrySettings } = useRegionalConfigContext();
 */
export function RegionalConfigProvider({ children }: { children: ReactNode }) {
  const app = useAppConfig();

  // Sync currency store with regional config from the global app context
  useEffect(() => {
    if (!app.ready || !app.regional) return;

    // Get sync function from store
    const { syncFromRegionalConfig } = useCurrencyStore.getState();

    // The backend /public endpoint provides the defaultTimezone
    const expectedTimezone = app.regional.defaultTimezone;

    if (typeof syncFromRegionalConfig === 'function') {
      syncFromRegionalConfig(app.regional);
    }

    // Verify sync worked after a short delay
    setTimeout(() => {
      const store = useCurrencyStore.getState();

      if (store.currency.timezone !== expectedTimezone) {
        localStorage.removeItem('currency-storage');
        if (typeof syncFromRegionalConfig === 'function') {
          syncFromRegionalConfig(app.regional);
        }
      }
    }, 200);
  }, [app.ready, app.regional]);

  const value: RegionalConfigContextValue = {
    defaultCurrency: app.regional?.defaultCurrency,
    defaultCountry: app.regional?.defaultCountry,
    timezone: app.regional?.defaultTimezone,
  };

  return (
    <RegionalConfigContext.Provider value={value}>
      {children}
    </RegionalConfigContext.Provider>
  );
}

/**
 * Hook to access regional configuration context
 * Must be used within RegionalConfigProvider
 */
export function useRegionalConfigContext() {
  const context = useContext(RegionalConfigContext);

  if (context === undefined) {
    throw new Error(
      'useRegionalConfigContext must be used within RegionalConfigProvider'
    );
  }

  return context;
}

/**
 * Hook to get current user's country settings
 * Can optionally detect from browser or use a stored preference
 */
export function useUserCountry(): string | undefined {
  const { defaultCountry } = useRegionalConfigContext();
  return defaultCountry;
}
